import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis,
  ResponsiveContainer, Tooltip, BarChart,
} from 'recharts';

// const API_BASE = 'http://localhost:8080';
const API_BASE = "https://altisonant.xyz/api";

// ── Types ─────────────────────────────────────────────────────────────────────

interface MarketPricePoint {
  time: number;
  price: number;
  volume: number;
}

interface InsiderCandidate {
  outcome: string;
  count: number;
  price: number;
  min_price: number;
  max_price: number;
  trade_value: number;
  slippage_pct: number;
  created_timestamp: number | null;
}

interface InsiderMarket {
  id: string;
  title: string;
  category: string;
  tags: string[];
  risk: number;
  description: string;
  candidates: InsiderCandidate[];
}

// ── API types ─────────────────────────────────────────────────────────────────

interface ApiMarketSummary {
  ticker: string;
  title: string;
  category: string;
  tags: string[];
  risk: number;
  reason: string;
  candidates: InsiderCandidate[];
}

interface ApiDetailResponse {
  history: MarketPricePoint[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CAT_COLORS: Record<string, string> = {
  'Culture':       'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  'Companies':     'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  'Tech & Science':'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

function catColor(category: string): string {
  return CAT_COLORS[category] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

const TOOLTIP_STYLE = {
  fontSize: 10,
  backgroundColor: 'rgba(30,30,30,0.9)',
  border: 'none',
  borderRadius: 4,
  color: '#fff',
  padding: '4px 6px',
};

function fmtDate(ms: number): string {
  const d = new Date(ms);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

// ── InsideTrackCard ───────────────────────────────────────────────────────────

const InsideTrackCard: React.FC<{
  market: InsiderMarket;
  onClick: () => void;
}> = ({ market, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="rounded-lg shadow-md overflow-hidden cursor-pointer transition-shadow hover:ring-2 hover:ring-indigo-500 bg-white dark:bg-gray-800"
    >
      <div className="p-3">
        {/* Badges */}
        <div className="flex items-center gap-1.5 mb-2">
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${catColor(market.category)}`}>
            {market.category}
          </span>
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
            market.risk >= 90
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
          }`}>
            Risk {market.risk}
          </span>
        </div>

        {/* Title */}
        <h3
          className="text-sm font-semibold leading-snug mb-2 overflow-hidden"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
        >
          {market.title}
        </h3>

        {/* Description */}
        <p
          className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug overflow-hidden"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
        >
          {market.description}
        </p>
      </div>
    </div>
  );
};

// ── InsideTrackDetail ─────────────────────────────────────────────────────────

const InsideTrackDetail: React.FC<{
  market: InsiderMarket;
  onBack: () => void;
}> = ({ market, onBack }) => {
  const [history, setHistory] = useState<MarketPricePoint[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/insider/markets/${market.id}/detail`)
      .then(r => r.json())
      .then((data: ApiDetailResponse) => {
        setHistory(data.history);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [market.id]);

  const chartData = history ?? [];

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 mb-3"
      >
        <ArrowLeft size={16} />
        Inside Track
      </button>

      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${catColor(market.category)}`}>
            {market.category}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            market.risk >= 90
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
          }`}>
            Risk {market.risk}
          </span>
        </div>
        <h2 className="text-xl font-bold leading-snug">{market.title}</h2>
      </div>

      {/* Why we track */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-4">
        <h3 className="text-sm font-semibold mb-1.5">Why We Track This</h3>
        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{market.description}</p>
      </div>

      {/* Insider Trade Candidates — YES / NO columns */}
      {(() => {
        const sorted = [...market.candidates].sort((a, b) =>
          (b.created_timestamp ?? 0) - (a.created_timestamp ?? 0)
        );
        const yesCands = sorted.filter(c => c.outcome === 'Yes');
        const noCands  = sorted.filter(c => c.outcome === 'No');

        const CandidateTable: React.FC<{ rows: InsiderCandidate[] }> = ({ rows }) =>
          rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="pb-2 pr-3 font-medium text-right">Value</th>
                    <th className="pb-2 pr-3 font-medium text-right">Contracts</th>
                    <th className="pb-2 pr-3 font-medium text-right">Price</th>
                    <th className="pb-2 pr-3 font-medium text-right">Slippage</th>
                    <th className="pb-2 font-medium text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                      <td className="py-2 pr-3 text-right font-mono">
                        ${c.trade_value >= 1000 ? `${(c.trade_value / 1000).toFixed(1)}k` : c.trade_value.toFixed(0)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {c.count >= 1000 ? `${(c.count / 1000).toFixed(1)}k` : c.count.toFixed(0)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {c.min_price.toFixed(2)}–{c.max_price.toFixed(2)}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-orange-500">
                        {(c.slippage_pct * 100).toFixed(1)}%
                      </td>
                      <td className="py-2 text-right text-gray-500 dark:text-gray-400">
                        {c.created_timestamp ? fmtDate(c.created_timestamp * 1000) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">None</div>
          );

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="text-sm font-semibold mb-3">
                <span className="text-green-600 dark:text-green-400">YES</span> Trades ({yesCands.length})
              </h3>
              <CandidateTable rows={yesCands} />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="text-sm font-semibold mb-3">
                <span className="text-red-600 dark:text-red-400">NO</span> Trades ({noCands.length})
              </h3>
              <CandidateTable rows={noCands} />
            </div>
          </div>
        );
      })()}

      {/* Charts */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <h3 className="text-sm font-semibold mb-3">
          Price &amp; Volume — 45 Days
          {loading && <span className="ml-2 text-[10px] font-normal text-gray-400">Loading…</span>}
        </h3>

        {chartData.length > 0 ? (
          <>
            <div className="text-[9px] text-gray-500 dark:text-gray-400 font-medium mb-1">Price (¢)</div>
            <ResponsiveContainer width="100%" height={120}>
              <ComposedChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={fmtDate}
                  tick={{ fontSize: 8 }}
                  height={16}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 8 }}
                  width={28}
                  tickFormatter={(v: number) => v.toFixed(0)}
                />
                <Tooltip
                  labelFormatter={(v: number) => fmtDate(v)}
                  formatter={(value: number) => [`${value.toFixed(0)}¢`, 'Price']}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Line
                  dataKey="price"
                  stroke="#6366F1"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="text-[9px] text-gray-500 dark:text-gray-400 font-medium mt-3 mb-1">Volume (contracts/day)</div>
            <ResponsiveContainer width="100%" height={80}>
              <BarChart data={chartData} margin={{ top: 0, right: 4, bottom: 0, left: 0 }}>
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={fmtDate}
                  tick={{ fontSize: 8 }}
                  height={16}
                />
                <YAxis
                  tick={{ fontSize: 8 }}
                  width={28}
                  tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                />
                <Tooltip
                  labelFormatter={(v: number) => fmtDate(v)}
                  formatter={(value: number) => [value.toLocaleString(), 'Volume']}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Bar
                  dataKey="volume"
                  fill="#6366F1"
                  fillOpacity={0.65}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          !loading && (
            <div className="text-xs text-gray-400 dark:text-gray-500 py-8 text-center">
              No trade data available
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ── InsideTrackView ───────────────────────────────────────────────────────────

const InsideTrackView: React.FC = () => {
  const [markets, setMarkets] = useState<InsiderMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/insider/markets`)
      .then(r => r.json())
      .then((data: ApiMarketSummary[]) => {
        setMarkets(data.map(m => ({
          id: m.ticker,
          title: m.title,
          category: m.category,
          tags: m.tags,
          risk: m.risk,
          description: m.reason,
          candidates: m.candidates,
        })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const selected = selectedId ? markets.find(m => m.id === selectedId) ?? null : null;

  if (selected) {
    return <InsideTrackDetail market={selected} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold">Inside Track</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {loading
              ? 'Loading markets…'
              : `${markets.length} markets with detected insider trade candidates`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-lg shadow-md bg-white dark:bg-gray-800 p-3 h-36 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {markets.map(market => (
              <InsideTrackCard
                key={market.id}
                market={market}
                onClick={() => setSelectedId(market.id)}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default InsideTrackView;
